import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import {
  ApiError,
  ApiSuccessfulLogin,
  GenericApiResponse,
} from 'src/app/interfaces/api';
import { IStrapiDocument, Login } from 'src/app/interfaces/strapi';
import { AuthService } from 'src/app/services/auth.service';
import StrapiService from 'src/app/services/strapi.service';
import { UserService } from 'src/app/services/user.service';
import { NgIf } from '@angular/common';
import { User } from '../../models/user.model';
import { SocialLoginButtonsComponent } from '../social-login-buttons/social-login-buttons.component';

export interface IUpdateExistingAccountData {
  postcode: string;
  universityId: string;
  accommodationId: string;
}

enum LoginModalEnum {
  defaultOtherErrorMessage = 'There was an error processing your login. Please refresh the page and try again.',
  defaultUpdateUserErrorMessage = 'There was an error updating your user. Please refresh the page and try again.',
}
@Component({
    selector: 'app-login-modal',
    templateUrl: './login-modal.component.html',
    styleUrls: ['./login-modal.component.scss'],
    imports: [
        NgIf,
        SocialLoginButtonsComponent,
        FormsModule,
        ReactiveFormsModule,
        RouterLink,
    ],
})
export class LoginModalComponent implements OnInit {
  public LoginModalEnum = LoginModalEnum;

  public modalData: Login = {} as Login;

  @Input() public updateExistingAccountData: IUpdateExistingAccountData =
    {} as IUpdateExistingAccountData;

  public submitted: boolean = false;

  public contentRetrievalError: boolean = false;

  public userUpdateError: boolean = false;

  public userUpdateErrorMessage: string =
    LoginModalEnum.defaultUpdateUserErrorMessage;

  public isLoading: boolean = false;

  public loginForm!: UntypedFormGroup;

  public unverified: boolean = false;

  public accountLocked: boolean = false;

  public loginNotFound: boolean = false;

  public verificationResent: boolean = false;

  public otherError: boolean = false;

  public otherErrorMessage: string = LoginModalEnum.defaultOtherErrorMessage;

  public loggingIn: boolean = false;

  constructor(
    private readonly strapi: StrapiService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly userService: UserService,
    private readonly authService: AuthService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.isLoading = false;
    this.strapi.getLoginPage().subscribe({
      next: (data: IStrapiDocument<Login>) => {
        this.modalData = data.data.attributes;
      },
      error: () => {
        this.contentRetrievalError = true;
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      },
    });

    this.authService.sanctumCSRF().subscribe({
      next: () => {
        this.authService.csrfLoaded = true;
      },
    });

    this.initializeForm();
  }

  /**
   * Submit the user login form and update any existing user details
   * @returns
   */
  public submit(): void {
    this.submitted = true;
    this.loginNotFound = false;

    if (this.loginForm.invalid) {
      return;
    }

    if (!this.authService.csrfLoaded) {
      this.otherError = true;
      return;
    }

    this.loggingIn = true;
    this.authService
      .emailLogin(
        String(this.loginForm.get('email')?.value),
        String(this.loginForm.get('password')?.value)
      )
      .subscribe({
        next: (user) => {
          this.updateExistingUserDetails(user as ApiSuccessfulLogin);
        },
        error: (err: ApiError) => {
          this.loggingIn = false;
          if (err.status === 400) {
            this.authService.migratedUserMustReset = true;
            this.router.navigate(['/forgotten-password']).finally(() => false);
          } else if (err.status === 403) {
            this.unverified = true;
          } else if (err.status === 401) {
            this.loginNotFound = true;

            if (err.error?.message === 'Account locked') {
              this.accountLocked = true;
            }
          } else {
            this.otherError = true;
          }
        },
      });
  }

  /**
   * Update existing user account details request
   */
  public updateExistingUserDetails(
    authenticatedUser: ApiSuccessfulLogin
  ): void {
    const tempUser = structuredClone(authenticatedUser.data.user);
    tempUser.uuid = authenticatedUser.data.user.uuid;
    if (this.updateExistingAccountData) {
      tempUser.postcode = this.updateExistingAccountData.postcode;
      tempUser.universityId = this.updateExistingAccountData.universityId;
      tempUser.accommodationId = this.updateExistingAccountData.accommodationId;
    }

    this.userService.updateUser(tempUser).subscribe({
      next: (data: GenericApiResponse<User>) => {
        const user = {
          ...authenticatedUser,
          data: { user: data.data, token: authenticatedUser.data.token },
        } as ApiSuccessfulLogin;
        this.authService.login(user);
        const route = this.authService.redirectAfterLogin ?? '/app';
        this.router.navigate([route]).finally(() => false);
      },
      error: (err: ApiError) => {
        this.userUpdateError = true;
        this.userUpdateErrorMessage =
          err.error?.message ?? LoginModalEnum.defaultUpdateUserErrorMessage;
      },
      complete: () => {
        this.loggingIn = false;
      },
    });
  }

  /**
   * Initialize user login form
   * @returns void
   */
  public initializeForm(): void {
    this.loginForm = this.formBuilder.group({
      email: [
        '',
        [Validators.email.bind(this), Validators.required.bind(this)],
      ],
      password: ['', [Validators.required.bind(this)]],
    });
  }

  public get formControl(): {
    [key: string]: AbstractControl;
  } {
    return this.loginForm.controls;
  }

  public resendVerification() {
    this.userService
      .resendUnauthenticatedVerificationEmail(
        String(this.loginForm.get('email')?.value)
      )
      .subscribe({
        next: () => {
          this.verificationResent = true;
        },
      });
  }
}
